import { Injectable } from '@angular/core';
import {
  DELETE_SPECY_CONTAINER_CAPACITY,
  GET_SPECY_CAPACITY_COMPLETENESS,
  GET_SPECY_CAPACITY_GRID,
  UPSERT_SPECY_CONTAINER_CAPACITY,
} from '@queries/species.graphql';
import { Apollo } from 'apollo-angular';
import { map, Observable, Subject, tap } from 'rxjs';
import {
  SpecyCapacityCellInput,
  SpecyCapacityCompleteness,
  SpecyCapacityGrid,
} from './species-capacity.types';

@Injectable({
  providedIn: 'root',
})
export class SpeciesCapacityService {
  private _capacityChanged$: Subject<number> = new Subject<number>();

  constructor(private apollo: Apollo) {}

  /** Emits the specyId every time a capacity cell of that species was written or deleted. */
  get capacityChanged$(): Observable<number> {
    return this._capacityChanged$.asObservable();
  }

  getGrid(specyId: number): Observable<SpecyCapacityGrid> {
    return this.apollo
      .query<{ SpecyCapacityGrid: SpecyCapacityGrid }>({
        query: GET_SPECY_CAPACITY_GRID,
        variables: { specyId },
        fetchPolicy: 'network-only',
      })
      .pipe(map(({ data }) => data.SpecyCapacityGrid));
  }

  /**
   * Only the keys that are defined are sent, so editing a capacity in the grid never
   * overwrites the density or the source stored by the spreadsheet import.
   */
  upsertCell(input: SpecyCapacityCellInput): Observable<unknown> {
    const data: SpecyCapacityCellInput = {
      specyId: input.specyId,
      dryTypeId: input.dryTypeId,
      quantityTypeId: input.quantityTypeId,
      capacityM3: input.capacityM3,
    };
    if (input.densityTonPerM3 !== undefined) {
      data.densityTonPerM3 = input.densityTonPerM3;
    }
    if (input.source !== undefined) {
      data.source = input.source;
    }

    return this.apollo
      .mutate<any>({
        mutation: UPSERT_SPECY_CONTAINER_CAPACITY,
        variables: { data },
      })
      .pipe(
        map(({ data: result }) => result.SpecyContainerCapacityUpsert),
        tap(() => this._capacityChanged$.next(input.specyId))
      );
  }

  deleteCell(
    specyId: number,
    dryTypeId: number,
    quantityTypeId: number
  ): Observable<boolean> {
    return this.apollo
      .mutate<any>({
        mutation: DELETE_SPECY_CONTAINER_CAPACITY,
        variables: { specyId, dryTypeId, quantityTypeId },
      })
      .pipe(
        map(({ data }) => data.SpecyContainerCapacityDelete as boolean),
        tap(() => this._capacityChanged$.next(specyId))
      );
  }

  getCompleteness(specyIds: number[]): Observable<SpecyCapacityCompleteness[]> {
    return this.apollo
      .query<{ SpecyCapacityCompleteness: SpecyCapacityCompleteness[] }>({
        query: GET_SPECY_CAPACITY_COMPLETENESS,
        variables: { specyIds },
        fetchPolicy: 'network-only',
      })
      .pipe(map(({ data }) => data.SpecyCapacityCompleteness));
  }
}
